import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { WarningCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { VoiceTypingButton } from "../components/VoiceTypingButton";

const DupNote = ({ hit, label, testid }) => (hit ? (
  <p data-testid={testid} className="flex items-start gap-1.5 text-xs font-medium text-rose-700 bg-rose-50 ring-1 ring-rose-200 rounded-lg px-2.5 py-2">
    <WarningCircle size={15} weight="fill" className="shrink-0 mt-px" />
    <span>This {label} already exists in the file of <Link to={`/customers/${hit.id}`} className="font-bold underline">{hit.name}</Link>. A file cannot be created twice.</span>
  </p>
) : null);

export default function NewCustomer() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "", address: "", mobile: "", pm_registration_no: "",
    consumer_no: "", payment_mode: "", meter_status: "", kw_capacity: "",
  });
  const [sealingFile, setSealingFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const [dup, setDup] = useState({});
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    const { consumer_no, pm_registration_no } = form;
    if (!consumer_no.trim() && !pm_registration_no.trim()) { setDup({}); return undefined; }
    const t = setTimeout(() => api.get("/customers/check-duplicate", { params: { consumer_no, pm_registration_no } })
      .then((r) => setDup(r.data)).catch(() => {}), 350);
    return () => clearTimeout(t);
  }, [form.consumer_no, form.pm_registration_no]); // eslint-disable-line react-hooks/exhaustive-deps
  const hasDup = !!(dup.consumer_no || dup.pm_registration_no);

  const submit = async (e) => {
    e.preventDefault();
    if (hasDup) { toast.error("Duplicate file — this customer already exists"); return; }
    if (form.meter_status === "smart_pre_installed" && !sealingFile) {
      toast.error("Please upload the sealing certificate for a pre-installed smart meter");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/customers", form);
      if (form.meter_status === "smart_pre_installed" && sealingFile) {
        const fd = new FormData();
        fd.append("doc_type", "sealing_certificate");
        fd.append("file", sealingFile);
        await api.post(`/customers/${data.id}/documents`, fd);
      }
      toast.success("File created. Hand over the physical copy, then tap 'Hand over to manager'.");
      navigate(`/customers/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto stagger-in">
      <h1 className="text-2xl sm:text-3xl font-heading font-extrabold tracking-tight text-slate-900 ">New customer file</h1>
      <p className="text-sm text-slate-500 mt-0.5 mb-5">Fill in the details from the customer's application. Fields marked * are required.</p>
      <form onSubmit={submit} className="border border-slate-200/80 rounded-2xl bg-card shadow-[0_1px_3px_rgba(15,23,42,0.04)] p-6 space-y-5">
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>Customer name *</Label>
            <Input data-testid="customer-name-input" value={form.name} onChange={(e) => set("name")(e.target.value)} required />
          </div>
          <div className="space-y-1.5">
            <Label>Mobile number *</Label>
            <Input data-testid="customer-mobile-input" value={form.mobile} onChange={(e) => set("mobile")(e.target.value)} required pattern="[0-9]{10}" title="10-digit mobile number" />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>Address *</Label>
          <div className="flex gap-2">
            <Input data-testid="customer-address-input" value={form.address} onChange={(e) => set("address")(e.target.value)} required className="flex-1" />
            <VoiceTypingButton onText={(t) => set("address")((form.address ? form.address + " " : "") + t)} />
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>PM Surya Ghar registration no. *</Label>
            <Input data-testid="customer-registration-input" value={form.pm_registration_no} onChange={(e) => set("pm_registration_no")(e.target.value)} required className={`font-mono ${dup.pm_registration_no ? "border-rose-400 ring-1 ring-rose-300" : ""}`} />
            <DupNote hit={dup.pm_registration_no} label="registration no." testid="dup-registration-error" />
          </div>
          <div className="space-y-1.5">
            <Label>Electricity consumer no. *</Label>
            <Input data-testid="customer-consumer-input" value={form.consumer_no} onChange={(e) => set("consumer_no")(e.target.value)} required className={`font-mono ${dup.consumer_no ? "border-rose-400 ring-1 ring-rose-300" : ""}`} />
            <DupNote hit={dup.consumer_no} label="consumer no." testid="dup-consumer-error" />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>System size (kW) *</Label>
          <Input data-testid="customer-kw-input" type="number" step="0.5" min="0" value={form.kw_capacity} onChange={(e) => set("kw_capacity")(e.target.value)} required placeholder="e.g. 3" className="font-mono" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>Payment mode *</Label>
            <Select value={form.payment_mode} onValueChange={set("payment_mode")} required>
              <SelectTrigger data-testid="payment-mode-select"><SelectValue placeholder="Select payment mode" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="finance">Finance</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Current meter *</Label>
            <Select value={form.meter_status} onValueChange={set("meter_status")} required>
              <SelectTrigger data-testid="meter-status-select"><SelectValue placeholder="Select meter type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="normal">Normal meter</SelectItem>
                <SelectItem value="smart_pre_installed">Smart meter already installed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        {form.meter_status === "smart_pre_installed" && (
          <div className="space-y-1.5 border border-amber-300 bg-amber-50 rounded-xl p-4">
            <Label className="text-amber-900">Sealing certificate *</Label>
            <p className="text-xs text-amber-800 mb-2">A smart meter is already installed, so the sealing certificate is required. The meter installation step will be skipped.</p>
            <Input data-testid="sealing-certificate-input" type="file" accept=".jpg,.jpeg,.png,.webp,.pdf" onChange={(e) => setSealingFile(e.target.files[0])} />
          </div>
        )}
        <Button data-testid="create-customer-submit" type="submit" className="w-full h-12 rounded-xl text-[15px]" disabled={loading || hasDup || !form.payment_mode || !form.meter_status}>
          {loading ? "Saving…" : "Create file"}
        </Button>
      </form>
    </div>
  );
}
